/*****************************************************************
 * Copyright (C) 2015-2017, 2023 VMware, Inc. All rights reserved.
 *****************************************************************/

/**
 * @fileoverview rtavprotocolservice.js -- rtavProtocolService
 * Service implement RTAV feature for WebClient, depend on MediaCapture, and MediaEncoder
 */

import RTAV from './logservice';

let rtavProtocolService = (function() {
   let constants = {},
      util = {},
      getSendingBinary,
      parseRecievedMessage;

   constants.rtavTypes = RTAV.constants.rtavTypes;
   constants.codecTypes = RTAV.constants.codecTypes;
   constants.streamHeaderLength = RTAV.constants.streamHeaderLength;

   util.convertU32 = function(n) {
      return [
         (n & 0xFF000000) >>> 24, (n & 0x00FF0000) >>> 8, (n & 0x0000FF00) << 8, (n & 0x000000FF) << 24
      ].reduce(function(a, b) {
         return a + b;
      });
   };

   /**
    * Contains protocol formation information for sending to get streamlized binary data.
    * @type {function}
    */
   getSendingBinary = (function() {
      let getNetWork,
         uint32ToUint8,
         getRtavControlPacket32,
         extendZeros,
         getDevPrefsNet,
         streamlizersMap;

      getNetWork = function(src) {
         let i,
            dst = [];

         for (i = 0; i < src.length; i++) {
            dst[i] = util.convertU32(src[i]);
         }
         return dst;
      };

      uint32ToUint8 = function(array32) {
         let i,
            length8 = array32.length * 4,
            buff8 = new Uint8Array((new Uint32Array(array32)).buffer),
            array8 = new Array(length8);

         for (i = 0; i < length8; i++) {
            array8[i] = buff8[i];
         }
         return array8;
      };

      getRtavControlPacket32 = function(type, dataArray) {
         let data,
            networkBytes,
            headerLength = 8,
            dataLength = dataArray.length * 4 + headerLength,
            typeCode = constants.rtavTypes[type];

         if (typeof typeCode !== 'number') {
            return;
         }
         RTAV.logService.debug("send " + type);
         data = [typeCode, dataLength].concat(dataArray);
         networkBytes = new Uint8Array((new Uint32Array(data)).buffer);
         return networkBytes;
      };

      extendZeros = function(length) {
         let i,
            result = new Array(length);
         for (i = 0; i < length; i++) {
            result[i] = 0;
         }
         return result;
      };

      getDevPrefsNet = function(localConfig) {
         let audioSettings = localConfig.audio,
            videoSettings = localConfig.video,
            audioPrefs,
            videoPrefs,
            audioInDevPrefsNet,
            videoCamDevPrefsNet;

         audioPrefs = {
            audFrameUnitLenMS: 20, // cAInUnitLenMS_Def = 20
            audFrameUnitCount: 50, // cAInCacheUnitCount_Def = 50
            queueLen: 10, // cAInQueueLen_Def = 10, but here set as 10
            bitsPerSample: audioSettings.bitsPerSample,
            channels: audioSettings.channels,
            inputStreams: 1,
            framesPerPacket: 1,
            samplesPerSec: audioSettings.sampleRate
         };
         videoPrefs = {
            fWidth: videoSettings.width,
            fHeight: videoSettings.height,
            frameRate: videoSettings.fps,
            tPauseMS: 0,
            queueLen: 10 // cWCamQueueLen_Def = 10
         };
         audioInDevPrefsNet = getNetWork([audioPrefs.audFrameUnitLenMS, audioPrefs.audFrameUnitCount, audioPrefs.queueLen, audioPrefs.bitsPerSample, audioPrefs.channels, audioPrefs.inputStreams, audioPrefs.framesPerPacket, audioPrefs.samplesPerSec].concat(extendZeros(20)));
         videoCamDevPrefsNet = getNetWork([videoPrefs.fWidth, videoPrefs.fHeight, videoPrefs.frameRate, videoPrefs.tPauseMS, videoPrefs.queueLen].concat(extendZeros(20)));

         return audioInDevPrefsNet.concat(videoCamDevPrefsNet);
      };

      streamlizersMap = {
         sendConfig: function(config) {
            let configArray = [8 * 4, RTAV.constants.AVPluginState['STOn'], config.versionNum, config.isEnabled, config.vdoRes.width, config.vdoRes.height, config.vdoFPS, config.reserved],
               networkArray = getNetWork(configArray);

            return getRtavControlPacket32('PMsgSetConfig', networkArray);
         },

         sendStartAudioAck: function() {
            let networkArray = [];
            return getRtavControlPacket32('PMsgStart_A_Ack', networkArray);
         },

         sendStartAudioNak: function() {
            let networkArray = [];
            return getRtavControlPacket32('PMsgStart_A_Ack_Err', networkArray);
         },

         sendStopAudioAck: function() {
            let networkArray = [];
            return getRtavControlPacket32('PMsgStop_A_Ack', networkArray);
         },

         sendStopAudioNak: function() {
            let networkArray = [];
            return getRtavControlPacket32('PMsgStop_A_Ack_Err', networkArray);
         },

         sendStartVideoAck: function(localConfig) {
            return getRtavControlPacket32('PMsgStart_V_Ack',
               getNetWork([localConfig.codecPref]).concat(getDevPrefsNet(localConfig)));
         },

         sendStartVideoNak: function() {
            let networkArray = [];
            return getRtavControlPacket32('PMsgStart_V_Ack_Err', networkArray);
         },

         sendStopVideoAck: function() {
            let networkArray = [];
            return getRtavControlPacket32('PMsgStop_V_Ack', networkArray);
         },

         sendStopVideoNak: function() {
            let networkArray = [];
            return getRtavControlPacket32('PMsgStop_V_Ack_Err', networkArray);
         },

         sendStream: function(messageData) {
            let streamData,
               streamHeader;

            if (!messageData || !messageData.streamData) {
               RTAV.logService.devError('trying to send a invalid stream');
               return null;
            }
            streamData = messageData.streamData;
            streamHeader = new Uint32Array(streamData.buffer, 0, 4);

            streamHeader[0] = constants.rtavTypes['PMsgBinData'];
            streamHeader[1] = streamData.length + constants.streamHeaderLength;
            streamHeader[2] = messageData.timeStamp;
            streamHeader[3] = messageData.dataCount;

            return new Uint8Array(streamData.buffer);
         },

         sendClientSettings: function(localConfig) {
            let codecPref = localConfig.codecPref;
            return getRtavControlPacket32('PMsgSetCliSettings', getNetWork([codecPref]).concat(getDevPrefsNet(localConfig)));
         },

         sendStopStream: function() {
            let networkArray = [];
            return getRtavControlPacket32('PMsgStopStream', networkArray);
         }
      };
      return function(type, data) {
         let getStream = streamlizersMap[type];
         if (typeof getStream !== 'function') {
            RTAV.logService.devError('unknown type to be streamlized');
            return null;
         }
         RTAV.logService.trace("generating response for " + type);
         return getStream(data);
      };
   }());

   /**
    * Contains reciever related logic and functions
    * @type {object}
    */
   parseRecievedMessage = (function() {
      let getMessageStruct,
         parseStruct,
         parseAgentGPOSettings,
         parseAVDevPrefs;

      getMessageStruct = function(message) {
         let headerLength = 8,
            header = new Uint32Array(message.data.slice(0, headerLength));
         if (header[1] !== message.data.byteLength) {
            return {
               type: "undefined"
            };
         }
         return {
            type: header[0],
            dataLength: header[1] - headerLength,
            data: message.data.slice(8)
         };
      };

      parseStruct = function(data, formats) {
         let i, j, k, format, result = {},
            array;
         for (i = 0, j = 0, k = 0; i < formats.length; i++) {
            format = formats[i];
            if (typeof format === 'object') {
               array = [];
               for (j = 0; j < format.length; j++, k++) {
                  array[j] = util.convertU32(data[k]);
               }
               result[format.name] = array;
            } else {
               result[format] = util.convertU32(data[k]);
               k++;
            }
         }
         return result;
      };

      parseAgentGPOSettings = function(rtavMessage) {
         let configArray = new Uint32Array(rtavMessage.data),
            format = ['dataLength', 'status', 'versionNum', 'isEnabled', 'vdoMaxFPS', 'vdoMaxResHeight', 'vdoMaxResWidth', 'vdoDefResHeight', 'vdoDefResWidth', {
               length: 20,
               name: 'reserved'
            }],
            agentGPOSettings;
         agentGPOSettings = parseStruct(configArray, format);
         if (agentGPOSettings.dataLength !== rtavMessage.dataLength) {
            return null;
         }
         RTAV.logService.log("parsed agent GPO settings vdoDefResWidth is: " + agentGPOSettings.vdoDefResWidth +
            ", vdoDefResHeight is: " + agentGPOSettings.vdoDefResHeight + ", vdoMaxResWidth is: "
            + agentGPOSettings.vdoMaxResWidth + ", vdoMaxResHeight is: " + agentGPOSettings.vdoMaxResHeight + ", vdoMaxFPS is: "
            + agentGPOSettings.vdoMaxFPS);
         return agentGPOSettings;
      };

      parseAVDevPrefs = function(rtavMessage) {
         let aVDevPrefsArray = new Uint32Array(rtavMessage.data),
            format = ['codecPrefs', 'audFrameUnitLenMS', 'audFrameUnitCount', 'queueLen', 'bitsPerSample',
                      'channels', 'inputStreams', 'framesPerPacket', 'samplesPerSec', 'cliSamplesPerSec',
                      {length: 19, name: 'reserved1'},
                      'fWidth', 'fHeight', 'frameRate', 'tPauseMS', 'queueLen', {length: 20, name: 'reserved2'}],
            aVDevPrefs;
         aVDevPrefs = parseStruct(aVDevPrefsArray, format);
         RTAV.logService.log("parsed agent codecPref is: " + aVDevPrefs.codecPrefs +
            ", bitsPerSample is: " + aVDevPrefs.bitsPerSample + ", channels is: "
            + aVDevPrefs.channels + ", width is: " + aVDevPrefs.fWidth + ", height is: "
            + aVDevPrefs.fHeight + ", samplesPerSec is: " + aVDevPrefs.samplesPerSec +
            ", framerate is: " + aVDevPrefs.frameRate);
         return aVDevPrefs;
      };

      return function(message) {
         let agentGPOSettings,
            aVDevPrefs,
            rtavMessage = getMessageStruct(message),
            parsedMessage = {
               type: rtavMessage.type,
               data: null
            },
            testAudioContext;

         switch (rtavMessage.type) {
            case constants.rtavTypes['PMsgGetConfig']:
               agentGPOSettings = parseAgentGPOSettings(rtavMessage);
               parsedMessage.data = agentGPOSettings;
               break;
            case constants.rtavTypes['PMsgGetCliSettings']:
               RTAV.logService.log("received agent message PMsgGetCliSettings");
               aVDevPrefs = parseAVDevPrefs(rtavMessage);
               parsedMessage.data = aVDevPrefs;
               RTAV.settings.audio.sampleRate = aVDevPrefs.samplesPerSec;
               RTAV.settings.audio.channels = aVDevPrefs.channels;
               RTAV.settings.audio.bitsPerSample = aVDevPrefs.bitsPerSample;

               testAudioContext  = new window.AudioContext();
               if (testAudioContext.sampleRate <  RTAV.settings.audio.sampleRate) {
                  RTAV.logService.log("AudioContext preferred sample rate " + testAudioContext.sampleRate + " smaller than agent expected " + RTAV.settings.audio.sampleRate);
                  RTAV.settings.audio.sampleRate = (testAudioContext.sampleRate >= 16000 ? 16000 : 8000);
               }
               testAudioContext.close();
               if (RTAV.settings.audio.sampleRate === 48000 &&
                  (parsedMessage.data.codecPrefs === constants.codecTypes["CodecNone"] ||
                  parsedMessage.data.codecPrefs === constants.codecTypes["CodecVmwTheoraSpeex"] ||
                  parsedMessage.data.codecPrefs === constants.codecTypes["CodecVmwH264Speex"])) {
                  RTAV.settings.audio.sampleRate = 16000;
               }
               break;
            case constants.rtavTypes['PMsgStart_A']:
               break;
            case constants.rtavTypes['PMsgStop_A']:
               break;
            case constants.rtavTypes['PMsgStart_V']:
               RTAV.logService.log("received agent message PMsgStart_V");
               aVDevPrefs = parseAVDevPrefs(rtavMessage);
               parsedMessage.data = aVDevPrefs;
               RTAV.settings.audio.sampleRate = aVDevPrefs.samplesPerSec;
               RTAV.settings.audio.channels = aVDevPrefs.channels;
               RTAV.settings.audio.bitsPerSample = aVDevPrefs.bitsPerSample;

               testAudioContext  = new window.AudioContext();
               if (testAudioContext.sampleRate <  RTAV.settings.audio.sampleRate) {
                  RTAV.logService.log("AudioContext preferred sample rate " + testAudioContext.sampleRate + " smaller than agent expected " + RTAV.settings.audio.sampleRate);
                  RTAV.settings.audio.sampleRate = (testAudioContext.sampleRate >= 16000 ? 16000 : 8000);
               }
               testAudioContext.close();
               if (RTAV.settings.audio.sampleRate === 48000 &&
                  (parsedMessage.data.codecPrefs === constants.codecTypes["CodecNone"] ||
                  parsedMessage.data.codecPrefs === constants.codecTypes["CodecVmwTheoraSpeex"] ||
                  parsedMessage.data.codecPrefs === constants.codecTypes["CodecVmwH264Speex"])) {
                  RTAV.settings.audio.sampleRate = 16000;
               }
               break;
            case constants.rtavTypes['PMsgStop_V']:
            case constants.rtavTypes['PMsgStartStream']:
            case constants.rtavTypes['PMsgStopStream']:
               break;
            default:
               RTAV.logService.error('unknown msg');
               RTAV.logService.error(rtavMessage);
               return null;
         }
         RTAV.logService.debug('recieve rtav message:' + JSON.stringify(parsedMessage));
         return parsedMessage;
      };
   }());

   return {
      /**
       * use to get rtav protocol message object into binary stream.
       * @param {string} type The message type
       * @param {object} data Optional The data that will be carried by the message
       * @return {object} This will returns the binary message constructed from the inputs
       */
      getSendingBinary: getSendingBinary,
      /**
       * use to parse rtav protocol message into object.
       * @param  {object} message The object contains binary message
       * @return {object} This will returns the parsed Object of input message
       */
      parseRecievedMessage: parseRecievedMessage
   };
}());

export default rtavProtocolService;